import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Button, InlineStack, Banner, TextField } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import { useParams } from "react-router-dom";
import { fetchFileDetails } from "containers/file/file.Slice";
import { declineSignRequest } from "services/clicktosign.service";
import { fetchAuthorizedFileDetails } from "containers/file-authorized/authorized-file.Slice";
import { fetchCabinetSummaryThunk } from "features/cabinet-tasks/cabinet-summary.slice";
import { fetchBusinessTripDetails } from "containers/business-trip-view-container/business-trip-container.slice";
import { clickSignErrorKey } from "utils/click-sign-errors";

interface ConfirmModalProps {
  fileId: string;
  tripId?: string;
  companyId?: string;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  fileId,
  tripId,
  companyId: propCompanyId,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId: paramCompanyId } = useParams();
  const companyId = propCompanyId || paramCompanyId;

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [declineReason, setDeclineReason] = useState<string>("");

  const handleToSendRequest = async () => {
    try {
      setIsLoading(true);
      const payload = {
        fileId,
        declineReason,
      };
      await declineSignRequest(companyId, payload);
      if (fileId && companyId) {
        dispatch(fetchFileDetails({ fileId, companyId }));
        dispatch(fetchAuthorizedFileDetails({ fileId, companyId }));
        if (tripId) {
          dispatch(fetchBusinessTripDetails({ tripId, companyId }));
        }
      }
      // Declined: the bell and the task list of the page's company move now, not at the next refresh.
      if (paramCompanyId) dispatch(fetchCabinetSummaryThunk(paramCompanyId));
      setSuccessMessage("ok");
      setIsLoading(false);
      closeModal();
    } catch (e) {
      setErrMsg(clickSignErrorKey(e, "error"));
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  return (
    <>
      <div className="form-warnings">
        {errMsg && (
          <div>
            <Banner tone="critical">{t(errMsg)}</Banner>
          </div>
        )}
        {successMessage && (
          <div>
            <Banner tone="info">{t(successMessage)}</Banner>
          </div>
        )}
      </div>
      <h1 style={{ textAlign: "center" }}>
        {t("are_you_sure_you_want_to_decline_document_signature_request")}
      </h1>

      <div style={{ margin: "16px 0" }}>
        <TextField
          label={t("reason_for_decline_signature")}
          value={declineReason}
          onChange={setDeclineReason}
          placeholder={t(
            "please_provide_a_reason_for_declining_the_signature_request"
          )}
          autoComplete=""
          multiline={3}
          requiredIndicator={true}
        />
      </div>

      <InlineStack align="space-between">
        <Button
          onClick={handleToSendRequest}
          loading={isLoading}
          disabled={!declineReason.trim()}
        >
          {t("Yes")}
        </Button>

        <Button onClick={closeModal}>{t("No")}</Button>
      </InlineStack>
    </>
  );
};

export default ConfirmModal;
